import { FolderIcon } from "@heroicons/react/outline";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { ToastType } from "@/enums";
import { useCreateFolder } from "@/queries";
import { displayErrorToast, displayToast } from "@/utils/toast";

interface CreateFolderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parentFolderId?: number | null;
  onSuccess?: () => void;
  redirect?: boolean;
}

export const CreateFolderDialog: React.FC<CreateFolderDialogProps> = ({
  open,
  onOpenChange,
  parentFolderId = null,
  onSuccess,
  redirect = true,
}) => {
  const [folderName, setFolderName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const authContext = useAuth();
  const userContext = useUser();
  const { userToken } = authContext!;
  const workspaceId = userContext.activeWorkspaceId!;

  const createFolderMutation = useCreateFolder(userToken!);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setFolderName("");
      setIsCreating(false);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  const handleCreate = async () => {
    if (!folderName.trim()) {
      displayToast("Please enter a folder name", ToastType.error);
      return;
    }

    setIsCreating(true);

    try {
      const response = await createFolderMutation.mutateAsync({
        name: folderName.trim(),
        workspace_id: workspaceId,
        parent_folder_id: parentFolderId ?? undefined,
      });

      onOpenChange(false);
      displayToast("Folder created successfully", ToastType.success);

      onSuccess?.();

      if (redirect) {
        const url = `/workspace/${workspaceId}/folder/${response.folder.id}`;
        navigate(url);
      }
    } catch (error) {
      displayErrorToast(error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleCreate();
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-medium">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100">
              <FolderIcon className="h-4 w-4 text-blue-600" />
            </div>
            Create New Folder
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="folder-name">Folder Name</Label>
            <Input
              ref={inputRef}
              id="folder-name"
              type="text"
              value={folderName}
              onChange={e => setFolderName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter folder name..."
              className="w-full"
              disabled={isCreating}
            />
          </div>

          {parentFolderId && (
            <div className="rounded-md bg-gray-50 p-3">
              <p className="text-sm text-gray-600">
                This folder will be created inside the current folder.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between">
          <Button variant="outline" onClick={handleCancel} disabled={isCreating}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!folderName.trim() || isCreating}
            className="min-w-[100px]"
          >
            {isCreating ? (
              <span className="flex items-center gap-2">
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Creating...
              </span>
            ) : (
              "Create Folder"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default observer(CreateFolderDialog);
